import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CheckIcon from "@mui/icons-material/Check";
import { getAllBrasilStates } from "~/utils/advanceRequest";
import { companyFiltersToSearch, DEFAULT_COMPANY_FILTERS, parseCompanyFilters } from "~/utils/packageFilters";
import { useAutoApplyFilters } from "~/shared/ui/filters/useAutoApplyFilters";

const STATES = getAllBrasilStates();

// Só os campos de filtro: ordem, página e pacote aberto ficam com a página (não disparam a barra).
const pick = ({ order, page, packageId, ...rest }) => rest;
const FILTER_DEFAULTS = pick(DEFAULT_COMPANY_FILTERS);
// Mesma normalização da URL (trim, dígitos, UF): o que a pessoa digita não some quando a URL volta normalizada.
const normalize = (d) => pick(parseCompanyFilters(companyFiltersToSearch({ ...DEFAULT_COMPANY_FILTERS, ...d })));
const TEXT_FIELDS = ["id", "doctorName", "crm"];

// Decisão U1: aplica sozinho (chips/UF/datas/switch 300 ms, texto 400 ms ou Enter); sem "Aplicar filtros".
export default function CompanyFilterBar({ value, companies, onApply, onClear }) {
  const { draft, setField, update, bindText, flush, clear, hasActive } = useAutoApplyFilters({
    value: pick(value),
    defaults: FILTER_DEFAULTS,
    onApply,
    onClear,
    textFields: TEXT_FIELDS,
    normalize,
  });

  const set = (field) => (event) => {
    const { type, checked, value: v } = event.target;
    setField(field, type === "checkbox" ? checked : v);
  };

  // Empresas inativas (auth/me companies[].active) só aparecem com o switch ligado (spec §4.2).
  const hasInactive = companies.some((c) => c.active === false);
  const visible = companies.filter((c) => c.active !== false || draft.includeInactive);
  const allIds = visible.map((c) => String(c.id));
  const toggleInactive = (event) => {
    const checked = event.target.checked;
    update((d) => {
      if (checked) return { ...d, includeInactive: true };
      // Desligou: a seleção perde as inativas; se sobrar exatamente todas as ativas, volta a "todas" ([]).
      const activeIds = companies.filter((c) => c.active !== false).map((c) => String(c.id));
      const kept = d.companyIds.filter((id) => activeIds.includes(id));
      return { ...d, includeInactive: false, companyIds: kept.length === activeIds.length ? [] : kept };
    });
  };
  const isOn = (id) => draft.companyIds.length === 0 || draft.companyIds.includes(id);
  const toggleCompany = (id) =>
    update((d) => {
      const current = d.companyIds.length ? d.companyIds : allIds;
      const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
      if (next.length === 0) return d; // pelo menos uma empresa fica marcada
      return { ...d, companyIds: next.length === allIds.length ? [] : allIds.filter((x) => next.includes(x)) };
    });

  return (
    <Box
      component="form"
      role="search"
      aria-label="Filtros das solicitações"
      onSubmit={(event) => {
        event.preventDefault();
        flush();
      }}
      sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}
    >
      {(visible.length > 1 || hasInactive) && (
        <Box role="group" aria-label="Empresas" sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5, width: "100%" }}>
          <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>Empresas:</Typography>
          {visible.map((c) => {
            const on = isOn(String(c.id));
            return (
              <Chip
                key={c.id}
                label={c.active === false ? `${c.name} · inativa` : c.name}
                icon={on ? <CheckIcon /> : undefined}
                color={on ? "primary" : "default"}
                variant={on ? "filled" : "outlined"}
                aria-pressed={on}
                onClick={() => toggleCompany(String(c.id))}
              />
            );
          })}
          <Button type="button" size="small" onClick={() => update((d) => ({ ...d, companyIds: [] }))}>Limpar</Button>
          {hasInactive && (
            <FormControlLabel
              sx={{ ml: 1 }}
              control={<Switch size="small" checked={Boolean(draft.includeInactive)} onChange={toggleInactive} />}
              label={<Typography sx={{ fontSize: 13 }}>Incluir empresas inativas</Typography>}
            />
          )}
        </Box>
      )}
      <TextField size="small" label="Nº do pacote" {...bindText("id")} inputProps={{ inputMode: "numeric" }} sx={{ width: 130 }} />
      <TextField size="small" label="Nome do médico" {...bindText("doctorName")} />
      <TextField size="small" label="CRM" {...bindText("crm")} sx={{ width: 110 }} />
      <TextField select size="small" label="UF do CRM" value={draft.crmUf} onChange={set("crmUf")} sx={{ width: 120 }}>
        <MenuItem value="">Todas</MenuItem>
        {STATES.map((s) => (
          <MenuItem key={s.uf} value={s.uf}>{s.uf}</MenuItem>
        ))}
      </TextField>
      <TextField size="small" type="date" label="Plantão de" InputLabelProps={{ shrink: true }} value={draft.shiftFrom} onChange={set("shiftFrom")} />
      <TextField size="small" type="date" label="Plantão até" InputLabelProps={{ shrink: true }} value={draft.shiftTo} onChange={set("shiftTo")} />
      <TextField size="small" type="date" label="Pedido de" InputLabelProps={{ shrink: true }} value={draft.requestFrom} onChange={set("requestFrom")} />
      <TextField size="small" type="date" label="Pedido até" InputLabelProps={{ shrink: true }} value={draft.requestTo} onChange={set("requestTo")} />
      <FormControlLabel control={<Checkbox checked={draft.pendingOnly} onChange={set("pendingOnly")} />} label="Pendentes de conferência" />
      {hasActive && <Button type="button" onClick={clear}>Limpar filtros</Button>}
    </Box>
  );
}
